import { useState, useEffect, useMemo } from "react";
import { useGetUser, useGetProfiles } from "@/services/usersService";
import { buildProfileSelectOptions } from "../../helpers";
import { UserFormStateProps, UseUserFormParams } from "./use-user-form.type";


const INITIAL_FORM_STATE: UserFormStateProps = {
  email: "",
  acquiringProfile: "",
  bankingProfile: "",
  status: "",
};

export const useUserForm = ({ mode, user, isOpen }: UseUserFormParams) => {
  const [formState, setFormState] = useState<UserFormStateProps>(INITIAL_FORM_STATE);
  const [initialized, setInitialized] = useState(false);

  const { data: profilesResponse, isLoading: isLoadingProfiles } = useGetProfiles(isOpen);
  const { data: userResponse, isLoading: isLoadingUser } = useGetUser(
    mode === "edit" && isOpen ? user?.id : 0,
  );

  const profiles = profilesResponse?.data ?? [];

  const acquiringProfileOptions = useMemo(
    () => buildProfileSelectOptions(profiles, "user_profile"),
    [profiles],
  );

  const bankingProfileOptions = useMemo(
    () => buildProfileSelectOptions(profiles, "banking_profile"),
    [profiles],
  );

  const hasAcquiringProfile = !!formState.acquiringProfile;
  const hasBankingProfile = !!formState.bankingProfile;

  const isLoading = isLoadingProfiles || (mode === "edit" && isLoadingUser);

  const isValid = useMemo(() => {
    if (mode === "create") {
      return !!formState.email && (hasAcquiringProfile || hasBankingProfile);
    }
    return hasAcquiringProfile || hasBankingProfile;
  }, [mode, formState.email, hasAcquiringProfile, hasBankingProfile]);

  const setField = <K extends keyof UserFormStateProps>(key: K, value: UserFormStateProps[K]) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
  };

  const toggleAcquiringProfile = () => {
    setFormState((prev) => ({
      ...prev,
      acquiringProfile: prev.acquiringProfile ? "" : prev.acquiringProfile,
    }));
  };

  const toggleBankingProfile = () => {
    setFormState((prev) => ({
      ...prev,
      bankingProfile: prev.bankingProfile ? "" : prev.bankingProfile,
    }));
  };

  useEffect(() => {
    if (mode === "edit" && userResponse?.data && isOpen && !initialized) {
      const userData = userResponse.data;
      setFormState({
        email: userData.email ?? "",
        acquiringProfile: userData.profile ?? "",
        bankingProfile: userData.bankingProfile ?? "",
        status: userData.status ?? "",
      });
      setInitialized(true);
    }
  }, [mode, userResponse, isOpen, initialized]);

  useEffect(() => {
    if (!isOpen) {
      setFormState(INITIAL_FORM_STATE);
      setInitialized(false);
    }
  }, [isOpen]);

  return {
    formState,
    setField,
    hasAcquiringProfile,
    hasBankingProfile,
    toggleAcquiringProfile,
    toggleBankingProfile,
    acquiringProfileOptions,
    bankingProfileOptions,
    isLoading,
    isValid,
  };
};
